import { useState, useMemo, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Download,
  Archive,
  ArchiveRestore,
  Tag,
  X,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { canEditContacts, canExportContacts } from '../../utils/permissions';
import { normalizePhone } from '../../utils/phone';
import ContactFormModal from './ContactFormModal';

const PAGE_SIZE = 10;
const CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'web', label: 'Web' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'email', label: 'E-mail' },
  { id: 'outros', label: 'Outros' },
];

export default function ContactsPanel({
  contacts = [],
  companies = [],
  tags = [],
  onSaveContact,
  onBulkUpdateContacts,
  onSaveCompany,
  onFetchContacts,
  total = 0,
}) {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ tag: '', empresa: '', canal: '', status: 'ativo' });
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [formContact, setFormContact] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [exportColumns, setExportColumns] = useState(new Set(['nome', 'telefone', 'email', 'empresa', 'tags', 'canal', 'status']));

  const canEdit = canEditContacts(user);
  const canExport = canExportContacts(user);

  useEffect(() => {
    onFetchContacts?.({
      q: search.trim(),
      status: filters.status,
      tag: filters.tag,
      empresa_id: filters.empresa,
      canal_origem: filters.canal,
      page,
      pageSize: PAGE_SIZE,
    });
  }, [search, filters, page, onFetchContacts]);

  const paginated = useMemo(() => contacts, [contacts]);
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

  const companiesList = Array.isArray(companies) ? companies : [];
  const tagsList = Array.isArray(tags) ? tags : [];
  const getCompanyName = (id) => companiesList.find((c) => c.id === id)?.nome_fantasia || '—';

  const getTagName = (id) => tags.find((t) => t.id === id)?.name || id;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginated.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paginated.map((c) => c.id)));
  };

  const handleCreate = () => {
    setFormContact(null);
    setShowForm(true);
  };

  const handleEdit = (contact) => {
    setFormContact(contact);
    setShowForm(true);
  };

  const handleSaveContact = async (data, isNew) => {
    await onSaveContact?.(data, isNew);
    setShowForm(false);
    setFormContact(null);
  };

  const handleBulkTags = async (addTags, removeTags) => {
    if (!addTags.length && !removeTags.length) return;
    const ids = Array.from(selectedIds);
    const selected = contacts.filter((c) => selectedIds.has(c.id));
    const updatesById = selected.reduce((acc, c) => {
      let nextTags = [...(c.tags || [])];
      removeTags.forEach((t) => (nextTags = nextTags.filter((x) => x !== t)));
      addTags.forEach((t) => {
        if (!nextTags.includes(t)) nextTags.push(t);
      });
      acc[c.id] = { tags: nextTags };
      return acc;
    }, {});
    await Promise.all(
      ids.map((id) => onBulkUpdateContacts?.([id], updatesById[id]))
    );
    setSelectedIds(new Set());
    setBulkAction(null);
  };

  const handleBulkArchive = async (archive) => {
    const ids = Array.from(selectedIds);
    await onBulkUpdateContacts?.(ids, { status: archive ? 'arquivado' : 'ativo' });
    setSelectedIds(new Set());
    setBulkAction(null);
  };

  const handleExport = () => {
    const list = selectedIds.size ? contacts.filter((c) => selectedIds.has(c.id)) : contacts;
    const cols = Array.from(exportColumns);
    const headers = cols.map((c) => {
      const map = { nome: 'Nome', telefone: 'Telefone', email: 'E-mail', empresa: 'Empresa', tags: 'Tags', canal: 'Canal', status: 'Status', observacoes_internas: 'Observações' };
      return map[c] || c;
    });
    const rows = list.map((c) =>
      cols.map((col) => {
        if (col === 'empresa') return getCompanyName(c.empresa_id);
        if (col === 'tags') return (c.tags || []).map(getTagName).join('; ');
        if (col === 'canal') return CHANNELS.find((ch) => ch.id === c.canal_origem)?.label || c.canal_origem || '';
        return (c[col] || '').toString();
      })
    );
    const csv = [headers.join(','), ...rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contatos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSelectedIds(new Set());
    setBulkAction(null);
  };

  const checkDuplicate = (field, value, excludeId) => {
    if (!value?.trim()) return false;
    const v = field === 'telefone' ? normalizePhone(value) : value.trim().toLowerCase();
    return contacts.some((c) => c.id !== excludeId && (field === 'telefone' ? normalizePhone(c.telefone) === v : (c.email || '').toLowerCase() === v));
  };

  return (
    <div className="flex-1 flex flex-col bg-appPage overflow-hidden">
      <header className="px-8 py-6 border-b border-appSurfaceBorder bg-appSurface shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-appText tracking-tight">Contatos</h1>
            <p className="text-sm text-appTextMuted mt-1">
              {total} contato(s) • Gerencie e exporte seus contatos
            </p>
          </div>
          <div className="flex gap-2">
            {canExport && (
              <button
                type="button"
                onClick={() => setBulkAction('export')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm border border-appSurfaceBorder text-appText hover:bg-appSurfaceBorder/30"
              >
                <Download size={18} />
                Exportar
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={handleCreate}
                className="flex items-center gap-2 px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90 shadow-lg"
              >
                <Plus size={18} />
                Novo contato
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mt-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-appTextMuted" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por nome, telefone ou e-mail..."
              className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}
            className="px-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText bg-white"
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="arquivado">Arquivado</option>
          </select>
          <select
            value={filters.tag}
            onChange={(e) => { setFilters((f) => ({ ...f, tag: e.target.value })); setPage(1); }}
            className="px-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText bg-white"
          >
            <option value="">Todas as tags</option>
            {tagsList.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <select
            value={filters.empresa}
            onChange={(e) => { setFilters((f) => ({ ...f, empresa: e.target.value })); setPage(1); }}
            className="px-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText bg-white"
          >
            <option value="">Todas as empresas</option>
            {companies.filter((c) => c.status === 'ativo').map((c) => (
              <option key={c.id} value={c.id}>{c.nome_fantasia}</option>
            ))}
          </select>
          <select
            value={filters.canal}
            onChange={(e) => { setFilters((f) => ({ ...f, canal: e.target.value })); setPage(1); }}
            className="px-4 py-2.5 rounded-xl border border-appSurfaceBorder text-appText bg-white"
          >
            <option value="">Todos os canais</option>
            {CHANNELS.map((ch) => (
              <option key={ch.id} value={ch.id}>{ch.label}</option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div className="mt-4 flex items-center gap-3 p-3 bg-appPrimaryLight/30 rounded-xl border border-appPrimary/20">
            <span className="text-sm font-bold text-appText">
              {selectedIds.size} selecionado(s)
            </span>
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => setBulkAction('tags')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-appSurface text-appText text-sm font-medium hover:bg-appSurfaceBorder/50"
                >
                  <Tag size={14} /> Tags
                </button>
                <button
                  type="button"
                  onClick={() => handleBulkArchive(true)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-appSurface text-appText text-sm font-medium hover:bg-appSurfaceBorder/50"
                >
                  <Archive size={14} /> Arquivar
                </button>
                <button
                  type="button"
                  onClick={() => handleBulkArchive(false)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-appSurface text-appText text-sm font-medium hover:bg-appSurfaceBorder/50"
                >
                  <ArchiveRestore size={14} /> Desarquivar
                </button>
              </>
            )}
            {canExport && (
              <button
                type="button"
                onClick={() => setBulkAction('export')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-appPrimary text-white text-sm font-medium hover:opacity-90"
              >
                <Download size={14} /> Exportar
              </button>
            )}
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto p-1.5 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
              aria-label="Limpar seleção"
            >
              <X size={18} />
            </button>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-auto p-8">
        <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-appSurfaceBorder bg-appPage/50">
                {canEdit && (
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={paginated.length > 0 && selectedIds.size === paginated.length}
                      onChange={toggleSelectAll}
                      className="rounded border-appSurfaceBorder text-appPrimary focus:ring-appPrimary"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Nome</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Telefone</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">E-mail</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Empresa</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Tags</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Canal</th>
                <th className="px-4 py-3 text-xs font-bold text-appTextMuted uppercase">Status</th>
                {canEdit && <th className="w-16 px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {paginated.map((contact) => (
                <tr
                  key={contact.id}
                  className="border-b border-appSurfaceBorder/50 hover:bg-appPage/30 transition-colors"
                >
                  {canEdit && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleSelect(contact.id)}
                        className="rounded border-appSurfaceBorder text-appPrimary focus:ring-appPrimary"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className="font-medium text-appText">{contact.nome || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-appText">{contact.telefone || '—'}</td>
                  <td className="px-4 py-3 text-sm text-appText">{contact.email || '—'}</td>
                  <td className="px-4 py-3 text-sm text-appText">{getCompanyName(contact.empresa_id)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(contact.tags || []).slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-appSurfaceBorder/50 text-appText"
                        >
                          {getTagName(t)}
                        </span>
                      ))}
                      {(contact.tags || []).length > 2 && (
                        <span className="text-xs text-appTextMuted">+{(contact.tags || []).length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-appText">
                    {CHANNELS.find((ch) => ch.id === contact.canal_origem)?.label || contact.canal_origem || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        contact.status === 'arquivado'
                          ? 'bg-slate-100 text-slate-600'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {contact.status === 'arquivado' ? 'Arquivado' : 'Ativo'}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleEdit(contact)}
                        className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
                        aria-label="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {paginated.length === 0 && (
            <div className="py-16 text-center text-appTextMuted">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-medium">Nenhum contato encontrado</p>
              <p className="text-sm mt-1">Ajuste os filtros ou crie um novo contato</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-appTextMuted">
              Página {page} de {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg border border-appSurfaceBorder text-appText disabled:opacity-50 disabled:cursor-not-allowed hover:bg-appSurfaceBorder/30"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg border border-appSurfaceBorder text-appText disabled:opacity-50 disabled:cursor-not-allowed hover:bg-appSurfaceBorder/30"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <ContactFormModal
          contact={formContact}
          companies={companiesList}
          tags={tagsList}
          channels={CHANNELS}
          contacts={contacts}
          onSave={handleSaveContact}
          onClose={() => { setShowForm(false); setFormContact(null); }}
          checkDuplicate={checkDuplicate}
          onAddCompany={onSaveCompany}
        />
      )}

      {bulkAction === 'tags' && (
        <BulkTagsModal
          tags={tagsList}
          selectedIds={selectedIds}
          contacts={contacts}
          onApply={handleBulkTags}
          onClose={() => setBulkAction(null)}
        />
      )}

      {bulkAction === 'export' && (
        <ExportModal
          columns={exportColumns}
          onColumnsChange={setExportColumns}
          onExport={handleExport}
          onClose={() => setBulkAction(null)}
          count={selectedIds.size || contacts.length}
        />
      )}
    </div>
  );
}

function BulkTagsModal({ tags, selectedIds, contacts, onApply, onClose }) {
  const [add, setAdd] = useState([]);
  const [remove, setRemove] = useState([]);

  const toggleAdd = (id) => setAdd((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const toggleRemove = (id) => setRemove((r) => (r.includes(id) ? r.filter((x) => x !== id) : [...r, id]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-appSurface rounded-2xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-appText mb-4">Aplicar tags em massa</h3>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-bold text-appTextMuted uppercase mb-2">Adicionar</p>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleAdd(t.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    add.includes(t.id) ? 'bg-appPrimary text-white border-appPrimary' : 'border-appSurfaceBorder text-appText'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-appTextMuted uppercase mb-2">Remover</p>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleRemove(t.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    remove.includes(t.id) ? 'bg-rose-100 text-rose-700 border-rose-200' : 'border-appSurfaceBorder text-appText'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <button
            type="button"
            onClick={() => onApply(add, remove)}
            className="flex-1 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90"
          >
            Aplicar
          </button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-sm">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function ExportModal({ columns, onColumnsChange, onExport, onClose, count }) {
  const allCols = [
    { id: 'nome', label: 'Nome' },
    { id: 'telefone', label: 'Telefone' },
    { id: 'email', label: 'E-mail' },
    { id: 'empresa', label: 'Empresa' },
    { id: 'tags', label: 'Tags' },
    { id: 'canal', label: 'Canal' },
    { id: 'status', label: 'Status' },
    { id: 'observacoes_internas', label: 'Observações' },
  ];

  const toggle = (id) => {
    const next = new Set(columns);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onColumnsChange(next);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-appSurface rounded-2xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-appText mb-2">Exportar contatos</h3>
        <p className="text-sm text-appTextMuted mb-4">
          {count} contato(s) serão exportados em CSV
        </p>
        <div>
          <p className="text-xs font-bold text-appTextMuted uppercase mb-2">Colunas a exportar</p>
          <div className="space-y-2">
            {allCols.map((c) => (
              <label key={c.id} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={columns.has(c.id)}
                  onChange={() => toggle(c.id)}
                  className="rounded border-appSurfaceBorder text-appPrimary"
                />
                <span className="text-sm text-appText">{c.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <button
            type="button"
            onClick={onExport}
            className="flex-1 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90"
          >
            Exportar CSV
          </button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-sm">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
